<script setup lang="ts">
import { AlertType } from "@/enums/alertTypes";
import { cn } from "@/helpers/cn";
import scrollToTarget from "@/helpers/scrollToTarget";
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/24/outline";
import { ref, watch } from "vue";
import { Collapse } from "vue-collapsed";

interface Props {
  type: AlertType;
  alert?: string;
  title?: string;
  subTitle?: string;
  footerMessage?: string;
  bordered?: boolean;
  automaticScroll?: boolean;
  hideNav?: boolean;
  titleStyle?: string;
  onOk?: () => unknown;
  classStyles?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  bordered: true,
  automaticScroll: false,
  classStyles: "rounded-md  border h-14 flex gap-4 items-center p-5 mt-5",
});
const understood = ref(false);
const containerRef = ref(null);

const configurations = {
  Warning: {
    icon: ExclamationCircleIcon,
    color: "yellow",
  },
  Error: {
    icon: ExclamationTriangleIcon,
    color: "red",
  },
  Info: {
    icon: InformationCircleIcon,
    color: "blue",
  },
};

const icon = configurations[props.type].icon;

const styles = {
  background: `bg-${configurations[props.type].color}-50`,
  icon: `text-${configurations[props.type].color}-400`,
  title: `text-${configurations[props.type].color}-800`,
  text: `text-${configurations[props.type].color}-700`,
  border: `border-${configurations[props.type].color}-400`,
  button: `border-${configurations[props.type].color}-400`,
  buttonBackground: `bg-${configurations[props.type].color}-100`,
};

const onOkHandler = () => {
  understood.value = true;
  props.onOk?.();
};

watch(understood, (value) => {
  if (!value && props.automaticScroll) {
    scrollToTarget(containerRef.value!);
  }
});
</script>

<template>
  <div
    v-motion
    :initial="{
      opacity: 0,
    }"
    :enter="{
      opacity: 1,
      transition: {
        delay: 100,
      },
    }"
    :class="[
      classStyles,
      styles.background,
      `${bordered ? styles.border : 'border-transparent'}`,
    ]"
    ref="containerRef"
  >
    <div class="flex">
      <div class="flex-shrink-0">
        <component :is="icon" :class="['h-5 w-5', styles.icon]" />
      </div>
      <div class="ml-3 flex flex-col justify-center">
        <h3
          :class="
            cn(
              'text-sm md:text-xs font-bold cursor-pointer',
              styles.title,
              props.titleStyle,
            )
          "
          @click="understood = !understood"
        >
          {{ title }}
        </h3>

        <Collapse :when="!understood" as="div" class="flex flex-col gap-2">
          <p v-if="subTitle" class="text-sm">{{ subTitle }}</p>

          <div v-if="alert" :class="['text-sm', styles.text]">
            {{ alert }}
          </div>

          <slot v-else name="alert" />

          <p v-if="footerMessage">{{ footerMessage }}</p>

          <nav v-if="!hideNav" class="flex items-center text-sm gap-4 mt-2">
            <button
              :class="[
                'p-1.5 px-3 rounded-md border hover:border-gray-400 fade-transition',
                styles.button,
                styles.buttonBackground,
              ]"
              @click.prevent="onOkHandler"
            >
              Entendido
            </button>
          </nav>
        </Collapse>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
